<script setup>
import { computed, onActivated, onBeforeUnmount, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import { useLanguageStore } from "@/stores/languageStore.js";
import {
  likePublicMap,
  listPublicMaps,
  recordPublicMapView,
  reportPublicMap,
  unlikePublicMap
} from "@/api/customMapApi.js";
import { localizedCreatorText } from "@/utils/localizedCreatorText.js";

const router = useRouter();
const languageStore = useLanguageStore();
const activePreview = ref(null);
const publicMaps = ref([]);
const searchQuery = ref("");
const feedLoading = ref(false);
const feedError = ref("");
const likeBusy = ref({});
const reportBusy = ref(false);
const reportMessage = ref("");
const reportDialogOpen = ref(false);
const reportReason = ref("");
const reportDetails = ref("");

function displayMapName(map) {
  return localizedCreatorText(languageStore, map?.name, "name");
}

function displayMapDescription(map) {
  return localizedCreatorText(languageStore, map?.description, "description");
}
const reportMessageText = computed(() => {
  const value = String(reportMessage.value || "");
  const translationKeys = new Set([
    "cannotReportOwnMap",
    "alreadyReported",
    "selectReportReason",
    "reportSubmitted"
  ]);
  return translationKeys.has(value) ? languageStore.t(value) : value;
});

const filteredMaps = computed(() => {
  const q = String(searchQuery.value || "").trim().toLowerCase();
  if (!q) return publicMaps.value;
  return publicMaps.value.filter(map => {
    const hay = `${map.name}\n${map.description}\n${map.author}`.toLowerCase();
    return hay.includes(q);
  });
});

function selectTool(tool) {
  if (tool === "star-map") {
    router.push({ name: "star-map-editor" });
    return;
  }
  if (tool === "level-map") {
    return;
  }
}

function formatCount(value) {
  const count = Number(value) || 0;
  if (count >= 10000) return `${(count / 10000).toFixed(count >= 100000 ? 0 : 1)}w`;
  if (count >= 1000) return `${(count / 1000).toFixed(count >= 10000 ? 0 : 1)}k`;
  return String(count);
}

function normalizePublicCard(raw) {
  return {
    id: String(raw?.id || ""),
    name: String(raw?.name || ""),
    description: String(raw?.description || ""),
    author: String(raw?.author || ""),
    authorUserId: Number(raw?.authorUserId) || null,
    authorAvatarUrl: String(raw?.authorAvatarUrl || ""),
    // Creator maps may retain a legacy generic `image` fallback. The uploaded
    // `imageUrl` is the authoritative cover and must match the map detail page.
    image: String(raw?.imageUrl || raw?.image || ""),
    levelCount: Math.max(0, Number(raw?.levelCount ?? raw?.nodeCount) || 0),
    builtLevelCount: Math.max(0, Number(raw?.builtLevelCount) || 0),
    plays: Math.max(0, Number(raw?.plays) || 0),
    views: Math.max(0, Number(raw?.views) || 0),
    likes: Math.max(0, Number(raw?.likes) || 0),
    likedByMe: Boolean(raw?.likedByMe),
    aspectRatio: Number(raw?.aspectRatio) || 1,
    updatedAt: raw?.updatedAt || null
  };
}

function comparePublicMaps(left, right) {
  // Newly published/updated work should be visible immediately instead of
  // sinking below older maps that already accumulated likes and views.
  const rightUpdated = Date.parse(right?.updatedAt || "") || 0;
  const leftUpdated = Date.parse(left?.updatedAt || "") || 0;
  if (rightUpdated !== leftUpdated) return rightUpdated - leftUpdated;

  const likesDiff = Number(right?.likes || 0) - Number(left?.likes || 0);
  if (likesDiff !== 0) return likesDiff;

  const viewsDiff = Number(right?.views || 0) - Number(left?.views || 0);
  if (viewsDiff !== 0) return viewsDiff;

  return String(right?.id || "").localeCompare(String(left?.id || ""));
}

async function loadPublicFeed() {
  feedLoading.value = true;
  feedError.value = "";
  try {
    const result = await listPublicMaps({ limit: 40 });
    publicMaps.value = (Array.isArray(result?.maps) ? result.maps : [])
      .map(normalizePublicCard)
      .filter(map => map.id)
      .sort(comparePublicMaps);
  } catch (error) {
    feedError.value = error?.message || languageStore.t("unavailable");
    publicMaps.value = [];
  } finally {
    feedLoading.value = false;
  }
}

async function openPreview(map) {
  activePreview.value = map;
  if (!map?.id) return;
  try {
    const result = await recordPublicMapView(map.id);
    map.views = Math.max(0, Number(result?.views) || map.views || 0);
  } catch {
    // Preview remains usable even if view analytics cannot be recorded.
  }
}

function closePreview() {
  activePreview.value = null;
  reportMessage.value = "";
}

function openReportDialog() {
  reportReason.value = "";
  reportDetails.value = "";
  reportMessage.value = "";
  reportDialogOpen.value = true;
}

function closeReportDialog() {
  if (!reportBusy.value) reportDialogOpen.value = false;
}

async function reportMap(map) {
  if (!map?.id || reportBusy.value) return;
  if (!reportReason.value) {
    reportMessage.value = languageStore.t("selectReportReason");
    return;
  }
  reportBusy.value = true;
  reportMessage.value = "";
  try {
    await reportPublicMap(map.id, { reason: reportReason.value, details: reportDetails.value });
    reportMessage.value = "reportSubmitted";
    window.setTimeout(() => { reportDialogOpen.value = false; }, 900);
  } catch (error) {
    reportMessage.value = error?.code === "OWN_MAP_REPORT"
      ? "cannotReportOwnMap"
      : error?.code === "MAP_ALREADY_REPORTED"
        ? "alreadyReported"
        : (error?.message || languageStore.t("unavailable"));
  } finally {
    reportBusy.value = false;
  }
}

function authorInitial(author) {
  return String(author || "")
    .replace(/^[@#]+/, "")
    .trim()
    .slice(0, 1)
    .toUpperCase() || "•";
}

function isLikeBusy(mapId) {
  return Boolean(likeBusy.value[String(mapId)]);
}

async function toggleLike(map) {
  if (!map?.id || isLikeBusy(map.id)) return;
  likeBusy.value = { ...likeBusy.value, [String(map.id)]: true };
  try {
    const result = map.likedByMe
      ? await unlikePublicMap(map.id)
      : await likePublicMap(map.id);
    map.likedByMe = Boolean(result?.likedByMe);
    map.likes = Math.max(0, Number(result?.likes) || 0);
    publicMaps.value = [...publicMaps.value].sort(comparePublicMaps);
  } catch (error) {
    feedError.value = error?.message || languageStore.t("unavailable");
  } finally {
    const next = { ...likeBusy.value };
    delete next[String(map.id)];
    likeBusy.value = next;
  }
}

async function enterPublicMap(map) {
  if (!map?.id) return;
  closePreview();
  await router.push({
    name: "custom-map-view",
    params: { mapId: map.id },
    query: {
      from: "workshop",
      mode: "play",
      access: "public"
    }
  });
}

onMounted(() => {
  loadPublicFeed();
  window.addEventListener("focus", loadPublicFeed);
  window.addEventListener("workshop-moderation-changed", loadPublicFeed);
  window.addEventListener("storage", handleModerationStorage);
});
onActivated(loadPublicFeed);
function handleModerationStorage(event) {
  if (event.key === "workshop-moderation-revision") loadPublicFeed();
}
onBeforeUnmount(() => {
  window.removeEventListener("focus", loadPublicFeed);
  window.removeEventListener("workshop-moderation-changed", loadPublicFeed);
  window.removeEventListener("storage", handleModerationStorage);
});
</script>

<template>
  <section class="workshop page-card">
    <div class="page-header workshop-header">
      <button class="back-button" type="button" @click="router.push({ name: 'home' })">
        ← {{ languageStore.t('back') }}
      </button>
      <h1 class="page-title">{{ languageStore.t('workshop') }}</h1>
      <span class="header-spacer" />
    </div>

    <div class="feed-heading">
      <div>
</div>
    </div>

    <div class="feed-heading">
      <div class="search-box">
        <input
          v-model.trim="searchQuery"
          type="search"
          enterkeyhint="search"
          class="search-input"
          :placeholder="languageStore.t('searchMaps')"
        />
        <svg class="search-icon" viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="10.5" cy="10.5" r="7" fill="none" stroke="currentColor" stroke-width="2" />
          <path d="m15.5 15.5 5 5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
        </svg>
      </div>
    </div>

    <p v-if="!feedLoading && filteredMaps.length === 0" class="empty-hint">
      {{ languageStore.t('noCustomMaps') }}
    </p>

    <div class="public-feed" :aria-label="languageStore.t('publicMaps')">
      <article
        v-for="map in filteredMaps"
        :key="map.id"
        class="public-map-card"
        tabindex="0"
        role="button"
        @click="openPreview(map)"
        @keydown.enter="openPreview(map)"
      >
        <div class="public-cover" :style="{ aspectRatio: String(map.aspectRatio || 1) }">
          <img :src="map.image" :alt="displayMapName(map)" />
          <span v-if="map.authorUserId === 21" class="my-work-badge">{{ languageStore.t('myCustomMaps') }}</span>
          <span class="level-chip" aria-hidden="true">
            <svg viewBox="0 0 24 24">
              <circle cx="6" cy="12" r="2" />
              <circle cx="12" cy="6" r="2" />
              <circle cx="18" cy="12" r="2" />
              <path d="M7.6 10.8 10.4 7.2M13.6 7.2l2.8 3.6" />
            </svg>
            <b>{{ formatCount(map.levelCount) }}</b>
          </span>
        </div>
        <div class="public-copy">
          <h2>
{{ displayMapName(map) }}
</h2>
          <p>
{{ displayMapDescription(map) }}
</p>
          <div class="public-meta">
            <span class="author-chip">
              <img v-if="map.authorAvatarUrl" :src="map.authorAvatarUrl" :alt="map.author" />
              <i v-else>{{ authorInitial(map.author) }}</i>
              <span>{{ map.author }}</span>
            </span>
            <span class="public-stats">
              <span
                class="stat-item"
                :title="languageStore.t('mapViews')"
                :aria-label="`${languageStore.t('mapViews')} ${formatCount(map.views)}`"
              >
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M2.8 12s3.2-5.2 9.2-5.2S21.2 12 21.2 12 18 17.2 12 17.2 2.8 12 2.8 12Z" />
                  <circle cx="12" cy="12" r="2.4" />
                </svg>
                <b>{{ formatCount(map.views) }}</b>
              </span>
              <button
                class="stat-item stat-like"
                type="button"
                :class="{ active: map.likedByMe }"
                :disabled="isLikeBusy(map.id)"
                :title="languageStore.t(map.likedByMe ? 'unlikeMap' : 'likeMap')"
                :aria-label="languageStore.t(map.likedByMe ? 'unlikeMap' : 'likeMap')"
                @click.stop="toggleLike(map)"
              >
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 20.2 4.7 13A4.7 4.7 0 0 1 11.3 6.3L12 7l.7-.7A4.7 4.7 0 0 1 19.3 13Z" /></svg>
                <b>{{ formatCount(map.likes) }}</b>
              </button>
            </span>
          </div>
        </div>
      </article>
    </div>

    <Teleport to="body">
      <div class="workshop-dock" :aria-label="languageStore.t('workshop')">
        <button class="dock-button" type="button" @click="selectTool('level-map')">
          <span class="dock-icon" aria-hidden="true">
            <svg class="dock-icon gallery-icon" viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="3" width="7" height="7" rx="1"></rect><rect x="14" y="3" width="7" height="7" rx="1"></rect><rect x="3" y="14" width="7" height="7" rx="1"></rect><rect x="14" y="14" width="7" height="7" rx="1"></rect></svg>
          </span>
          <span>
            <strong>{{ languageStore.t('publicMaps') }}</strong>
            <small></small>
          </span>
        </button>

        <button class="dock-button" type="button" @click="selectTool('star-map')">
          <span class="dock-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24">
              <circle cx="5" cy="15" r="1.4" />
              <circle cx="9" cy="8" r="1.4" />
              <circle cx="15" cy="5" r="1.4" />
              <circle cx="19" cy="12" r="1.4" />
              <circle cx="14" cy="18" r="1.4" />
              <path d="M6.1 13.9 8 9.2M10.3 7.4l3.3-1.6M16.2 6.1l2 4.6M18 13.3l-3 3.5M12.8 17.2 6.4 15.5" />
            </svg>
          </span>
          <span>
            <strong>{{ languageStore.t('workshopWorkbench') }}</strong>
            <small></small>
          </span>
        </button>
      </div>

      <div v-if="activePreview" class="preview-backdrop" @click.self="closePreview">
        <section class="preview-card" role="dialog" aria-modal="true" :aria-label="displayMapName(activePreview)">
          <button class="preview-close" type="button" @click="closePreview">×</button>
          <img :src="activePreview.image" :alt="displayMapName(activePreview)" />
          <div class="preview-content">
            <h2>{{ displayMapName(activePreview) }}</h2>
            <p>{{ displayMapDescription(activePreview) }}</p>
            <div class="preview-author">
              <img v-if="activePreview.authorAvatarUrl" :src="activePreview.authorAvatarUrl" :alt="activePreview.author" />
              <i v-else>{{ authorInitial(activePreview.author) }}</i>
              <span>{{ activePreview.author }}</span>
            </div>
            <div class="preview-stats" aria-hidden="true">
              <span class="preview-stat">
                <svg viewBox="0 0 24 24"><circle cx="6" cy="12" r="2" /><circle cx="12" cy="6" r="2" /><circle cx="18" cy="12" r="2" /><path d="M7.6 10.8 10.4 7.2M13.6 7.2l2.8 3.6" /></svg>
                <b>{{ formatCount(activePreview.levelCount) }}</b>
              </span>
              <span class="preview-stat">
                <svg viewBox="0 0 24 24"><rect x="4" y="5" width="7" height="6" rx="1" /><rect x="13" y="5" width="7" height="6" rx="1" /><rect x="8.5" y="13" width="7" height="6" rx="1" /></svg>
                <b>{{ formatCount(activePreview.builtLevelCount) }}</b>
              </span>
              <span class="preview-stat" :title="languageStore.t('mapViews')">
                <svg viewBox="0 0 24 24">
                  <path d="M2.8 12s3.2-5.2 9.2-5.2S21.2 12 21.2 12 18 17.2 12 17.2 2.8 12 2.8 12Z" />
                  <circle cx="12" cy="12" r="2.4" />
                </svg>
                <b>{{ formatCount(activePreview.views) }}</b>
              </span>
              <span class="preview-stat" :class="{ active: activePreview.likedByMe }">
                <svg viewBox="0 0 24 24"><path d="M12 20.2 4.7 13A4.7 4.7 0 0 1 11.3 6.3L12 7l.7-.7A4.7 4.7 0 0 1 19.3 13Z" /></svg>
                <b>{{ formatCount(activePreview.likes) }}</b>
              </span>
            </div>
            <div class="preview-actions">
              <button
                class="preview-like-button"
                type="button"
                :class="{ active: activePreview.likedByMe }"
                :disabled="isLikeBusy(activePreview.id)"
                :title="languageStore.t(activePreview.likedByMe ? 'unlikeMap' : 'likeMap')"
                :aria-label="languageStore.t(activePreview.likedByMe ? 'unlikeMap' : 'likeMap')"
                @click="toggleLike(activePreview)"
              >
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 20.2 4.7 13A4.7 4.7 0 0 1 11.3 6.3L12 7l.7-.7A4.7 4.7 0 0 1 19.3 13Z" /></svg>
                <b>{{ formatCount(activePreview.likes) }}</b>
              </button>
              <button class="choose-public-map" type="button" @click="enterPublicMap(activePreview)">{{ languageStore.t('enterMap') }}</button>
            </div>
            <button class="report-button" type="button" :disabled="reportBusy" @click="openReportDialog">
              ⚑ {{ languageStore.t('reportMap') }}
            </button>
            <p v-if="reportMessage" class="report-message" role="status">{{ reportMessageText }}</p>
          </div>
        </section>
      </div>

      <div v-if="reportDialogOpen && activePreview" class="report-backdrop" @click.self="closeReportDialog">
        <section class="report-dialog" role="dialog" aria-modal="true" :aria-label="languageStore.t('reportMap')">
          <button class="report-close" type="button" @click="closeReportDialog">×</button>
          <h2>{{ languageStore.t('reportMap') }}</h2>
          <p>{{ languageStore.t('reportPrompt') }}</p>
          <label v-for="reason in ['sexual','violence','hate','illegal','spam','copyright','other']" :key="reason" class="reason-option">
            <input v-model="reportReason" type="radio" name="report-reason" :value="reason" />
            <span>{{ languageStore.t(`reportReason_${reason}`) }}</span>
          </label>
          <textarea v-model="reportDetails" maxlength="300" :placeholder="languageStore.t('reportDetails')" />
          <p v-if="reportMessage" class="report-message" role="status">{{ reportMessageText }}</p>
          <button class="report-submit" type="button" :disabled="reportBusy || !reportReason" @click="reportMap(activePreview)">
            {{ reportBusy ? languageStore.t('saving') : languageStore.t('submitReport') }}
          </button>
        </section>
      </div>
    </Teleport>
  </section>
</template>

<style scoped>
.workshop {
  position: relative;
  min-height: calc(100dvh - 48px);
  padding: 22px 22px 118px;
  overflow: visible;
}
.workshop-header { position: relative; z-index: 4; margin-bottom: 10px; }
.header-spacer { width: 54px; }
.feed-heading { display:flex; align-items:center; justify-content:space-between; margin: 2px 2px 14px; }
.feed-heading div { min-width:0; display:flex; align-items:baseline; gap:10px; }
.feed-heading strong { color:#f6ffff; font-size:18px; letter-spacing:.02em; }
.feed-heading span { color:rgba(226,255,252,.48); font-size:12px; }

.search-box { position:relative; width:100%; }
.search-input {
  width:100%;
  height:40px;
  padding:0 38px 0 14px;
  border:1px solid rgba(104,240,232,.25);
  border-radius:12px;
  background:rgba(4,18,31,.72);
  color:#f6ffff;
  font-size:14px;
  outline:none;
  transition:border-color .16s ease, background .16s ease;
}
.search-input::placeholder { color:rgba(226,255,252,.42); }
.search-input:focus { border-color:rgba(82,246,232,.65); background:rgba(4,24,40,.84); }
.search-icon {
  position:absolute;
  right:12px;
  top:50%;
  width:18px;
  height:18px;
  transform:translateY(-50%);
  color:rgba(226,255,252,.55);
  pointer-events:none;
}

.public-feed { display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); gap:14px; }
.empty-hint { text-align:center; color:rgba(226,255,252,.55); padding:24px 12px; font-size:14px; }
.public-map-card {
  width:100%;
  display:block;
  margin:0;
  overflow:hidden;
  border:1px solid rgba(104,240,232,.13);
  border-radius:16px;
  color:#fff;
  background:linear-gradient(180deg,rgba(8,30,45,.86),rgba(4,18,31,.94));
  box-shadow:0 10px 24px rgba(0,0,0,.14);
  cursor:pointer;
  vertical-align:top;
  transition:transform .16s ease,border-color .16s ease;
}
.public-map-card:hover,.public-map-card:focus-visible { transform:translateY(-2px); border-color:rgba(82,246,232,.42); outline:none; }
.public-cover { position:relative; width:100%; overflow:hidden; background:#020915; }
.public-cover img { width:100%; height:100%; display:block; object-fit:cover; }
.my-work-badge { position:absolute; z-index:2; left:8px; top:8px; padding:4px 8px; border:1px solid rgba(127,248,239,.68); border-radius:999px; color:#eafffd; background:rgba(3,45,58,.9); box-shadow:0 3px 12px rgba(0,0,0,.3); font-size:10px; font-weight:900; }
.level-chip { position:absolute; right:8px; top:8px; min-height:25px; padding:4px 7px; border:1px solid rgba(255,255,255,.15); border-radius:999px; background:rgba(0,8,17,.68); color:rgba(245,255,254,.9); display:flex; align-items:center; gap:4px; font-size:10px; font-weight:800; backdrop-filter:blur(6px); }
.level-chip svg { width:13px; height:13px; fill:none; stroke:currentColor; stroke-width:1.7; stroke-linecap:round; stroke-linejoin:round; }
.level-chip b { font:inherit; }
.public-copy { padding:10px 11px 11px; }
.public-copy h2 { margin:0 0 5px; overflow:hidden; color:#f7ffff; font-size:14px; line-height:1.32; font-weight:850; white-space:nowrap; text-overflow:ellipsis; }
.public-copy p { margin:0; display:-webkit-box; overflow:hidden; color:rgba(235,255,253,.61); font-size:11px; line-height:1.52; -webkit-line-clamp:2; -webkit-box-orient:vertical; }
.public-meta { margin-top:9px; display:flex; align-items:center; justify-content:space-between; gap:8px; color:rgba(218,245,243,.42); font-size:9px; white-space:nowrap; }
.author-chip { min-width:0; display:flex; align-items:center; gap:5px; overflow:hidden; text-overflow:ellipsis; }
.author-chip i, .author-chip img { width:18px; height:18px; flex:0 0 auto; border-radius:50%; }
.author-chip i { display:grid; place-items:center; background:rgba(82,246,232,.14); color:#8ffdf3; font-size:9px; font-style:normal; font-weight:900; }
.author-chip img { display:block; object-fit:cover; background:rgba(82,246,232,.08); }
.author-chip > span { min-width:0; overflow:hidden; white-space:nowrap; text-overflow:ellipsis; }
.public-stats { display:flex; align-items:center; gap:7px; }
.stat-item { min-width:0; display:inline-flex; align-items:center; gap:3px; color:rgba(218,245,243,.55); }
.stat-item svg { width:13px; height:13px; fill:none; stroke:currentColor; stroke-width:1.8; stroke-linecap:round; stroke-linejoin:round; }
.stat-item b { font:inherit; font-weight:800; }
button.stat-item { padding:0; border:0; background:transparent; font:inherit; cursor:pointer; }
button.stat-item:disabled { cursor:default; opacity:.65; }
.stat-like.active { color:#8ffdf3; }
.stat-like.active svg { fill:currentColor; }

.workshop-dock {
  position:fixed;
  z-index:30;
  left:50%;
  bottom:max(12px,env(safe-area-inset-bottom));
  width:min(520px,calc(100vw - 34px));
  transform:translateX(-50%);
  display:grid;
  grid-template-columns:repeat(2,minmax(0,1fr));
  gap:10px;
  padding:8px;
  border:1px solid rgba(82,246,232,.2);
  border-radius:18px;
  background:rgba(3,15,27,.9);
  box-shadow:0 10px 32px rgba(0,0,0,.38);
  backdrop-filter:blur(16px);
}
.dock-button { min-width:0; min-height:58px; padding:8px 10px; border:1px solid rgba(82,246,232,.24); border-radius:13px; color:#fff; background:linear-gradient(180deg,rgba(13,58,73,.78),rgba(5,31,47,.88)); display:flex; align-items:center; justify-content:center; gap:9px; text-align:left; }
.dock-button:hover { border-color:rgba(82,246,232,.58); }
.dock-icon { width:24px; height:24px; flex:0 0 auto; color:#8ffdf3; }
.dock-icon svg { width:100%; height:100%; fill:none; stroke:currentColor; stroke-width:1.5; stroke-linecap:round; stroke-linejoin:round; }
.dock-button > span:last-child { min-width:0; display:flex; flex-direction:column; gap:2px; }
.dock-button strong { overflow:hidden; font-size:12px; line-height:1.15; white-space:nowrap; text-overflow:ellipsis; }
.dock-button small { color:rgba(226,255,252,.43); font-size:8px; line-height:1.1; white-space:nowrap; }

.preview-backdrop { position:fixed; z-index:80; inset:0; display:grid; place-items:center; padding:20px; background:rgba(0,5,13,.74); backdrop-filter:blur(8px); }
.preview-card { position:relative; width:min(520px,100%); max-height:min(760px,88dvh); overflow:auto; border:1px solid rgba(82,246,232,.25); border-radius:20px; background:#071522; box-shadow:0 24px 70px rgba(0,0,0,.5); }
.preview-card > img { width:100%; max-height:50dvh; display:block; object-fit:cover; background:#020813; }
.preview-close { position:absolute; z-index:2; top:10px; right:10px; width:34px; height:34px; border:1px solid rgba(255,255,255,.16); border-radius:50%; color:#fff; background:rgba(0,8,17,.7); font-size:22px; line-height:1; }
.preview-content { padding:16px; }
.preview-content h2 { margin:0 0 8px; font-size:20px; }
.preview-content p { margin:0; color:rgba(238,255,253,.68); font-size:13px; line-height:1.65; }
.preview-author { margin-top:12px; min-width:0; display:flex; align-items:center; gap:7px; color:rgba(225,255,252,.62); font-size:11px; }
.preview-author i, .preview-author img { width:22px; height:22px; flex:0 0 auto; border-radius:50%; }
.preview-author i { display:grid; place-items:center; background:rgba(82,246,232,.14); color:#8ffdf3; font-size:10px; font-style:normal; font-weight:900; }
.preview-author img { display:block; object-fit:cover; background:rgba(82,246,232,.08); }
.preview-author span { overflow:hidden; white-space:nowrap; text-overflow:ellipsis; }
.preview-stats { margin-top:12px; display:flex; align-items:center; gap:16px; color:rgba(225,255,252,.56); }
.preview-stat { display:inline-flex; align-items:center; gap:5px; }
.preview-stat svg { width:16px; height:16px; fill:none; stroke:currentColor; stroke-width:1.7; stroke-linecap:round; stroke-linejoin:round; }
.preview-stat b { font-size:11px; }
.preview-stat.active { color:#8ffdf3; }
.preview-stat.active svg { fill:currentColor; }
.preview-actions { margin-top:16px; display:grid; grid-template-columns:auto minmax(0,1fr); gap:9px; }
.preview-like-button { min-width:66px; min-height:46px; padding:0 13px; border:1px solid rgba(82,246,232,.32); border-radius:13px; color:rgba(239,255,255,.72); background:rgba(10,47,60,.74); display:inline-flex; align-items:center; justify-content:center; gap:6px; }
.preview-like-button svg { width:18px; height:18px; fill:none; stroke:currentColor; stroke-width:1.8; stroke-linecap:round; stroke-linejoin:round; }
.preview-like-button b { font-size:11px; }
.preview-like-button.active { color:#8ffdf3; border-color:rgba(82,246,232,.62); }
.preview-like-button.active svg { fill:currentColor; }
.preview-like-button:disabled { opacity:.6; cursor:default; }
.report-button { margin-top:10px; border:0; padding:7px 2px; color:rgba(255,255,255,.5); background:transparent; font-size:11px; }
.report-button:hover { color:#ff9aad; }
.report-button:disabled { opacity:.45; }
.report-message { margin-top:4px !important; color:#8ffdf3 !important; font-size:11px !important; }
.report-backdrop { position:fixed; z-index:100; inset:0; display:grid; place-items:center; padding:18px; background:rgba(0,5,13,.82); }
.report-dialog { position:relative; width:min(440px,100%); padding:22px; border:1px solid rgba(82,246,232,.3); border-radius:18px; color:#edfffd; background:#071a29; }
.report-dialog h2 { margin:0 0 6px; }
.report-dialog > p { color:rgba(255,255,255,.62); font-size:12px; }
.report-close { position:absolute; top:10px; right:12px; border:0; color:#fff; background:transparent; font-size:25px; }
.reason-option { display:flex; align-items:center; gap:10px; min-height:37px; border-bottom:1px solid rgba(255,255,255,.06); font-size:13px; }
.reason-option input { accent-color:#52f6e8; }
.report-dialog textarea { width:100%; min-height:76px; margin-top:12px; padding:10px; border:1px solid rgba(82,246,232,.25); border-radius:10px; resize:vertical; color:#fff; background:#04131f; }
.report-submit { width:100%; min-height:44px; margin-top:12px; border:1px solid #52f6e8; border-radius:12px; color:#fff; background:#0b6170; font-weight:800; }
.report-submit:disabled { opacity:.45; }
.choose-public-map {
  width:100%;
  min-height:46px;
  margin-top:0;
  border:1px solid rgba(82,246,232,.52);
  border-radius:13px;
  color:#efffff;
  background:linear-gradient(180deg,rgba(18,95,110,.88),rgba(4,48,65,.92));
  font-size:14px;
  font-weight:800;
}
.choose-public-map:disabled {
  opacity:.72;
  cursor:not-allowed;
}

@media (max-width: 760px) {
  .public-feed { grid-template-columns:repeat(2,minmax(0,1fr)); gap:10px; }
}

@media (max-width: 560px) {
  .workshop { min-height:calc(100dvh - 28px); padding:14px 12px 104px; }
  .workshop-header { margin-bottom:8px; }
  .feed-heading { margin:0 2px 10px; }
  .feed-heading strong { font-size:16px; }
  .feed-heading span { display:none; }
  .search-input { height:38px; font-size:13px; }
  .public-feed { grid-template-columns:repeat(2,minmax(0,1fr)); gap:9px; }
  .public-map-card { margin-bottom:9px; border-radius:13px; }
  .public-copy { padding:8px 8px 9px; }
  .public-copy h2 { margin-bottom:4px; font-size:13px; }
  .public-copy p { font-size:10.5px; line-height:1.45; -webkit-line-clamp:2; }
  .public-meta { margin-top:7px; gap:5px; font-size:8px; }
  .author-chip i, .author-chip img { width:16px; height:16px; }
  .author-chip i { font-size:8px; }
  .level-chip { right:6px; top:6px; padding:3px 6px; font-size:9px; }
  .workshop-dock { bottom:max(8px,env(safe-area-inset-bottom)); width:calc(100vw - 22px); gap:7px; padding:6px; border-radius:16px; }
  .dock-button { min-height:52px; padding:7px 8px; gap:7px; border-radius:11px; }
  .dock-icon { width:21px; height:21px; }
  .dock-button strong { font-size:11px; }
  .dock-button small { font-size:11px;}
  .preview-backdrop { padding:14px; }
  .preview-card { border-radius:16px; }
  .preview-content { padding:13px; }
  .preview-content h2 { font-size:18px; }
  .preview-content p { font-size:12px; }
}

@media (max-width: 360px) {
  .public-copy h2 { font-size:12px; }
  .public-copy p { font-size:10px; }
  .public-stats { display:flex; }
  .dock-button strong { font-size:16px; }
}

/* REMOVE_RED_FRAME_SUBTITLES_V1_START */
.tile-subtitle,
.nav-subtitle,
.feature-subtitle,
.card-subtitle,
.workshop-subtitle,
.map-subtitle,
.empty-description,
.empty-hint,
.helper-text,
.form-hint,
.status-hint {
  display: none !important;
}
/* REMOVE_RED_FRAME_SUBTITLES_V1_END */


/* REMOVE_RED_FRAME_REMAINS_V2_START */
.tile-subtitle,
.nav-subtitle,
.feature-subtitle,
.card-subtitle,
.map-subtitle,
.workshop-subtitle,
.empty-description,
.empty-hint,
.helper-text,
.form-hint,
.status-hint,
.description,
.description-text,
.description-label,
.map-description,
.map-description-label,
.map-description-text,
.generated-hint,
.generated-status,
.generation-hint,
.generation-status,
.special-level-card,
.special-level,
.special-chapter,
.chapter-endpoint {
  display: none !important;
}
/* REMOVE_RED_FRAME_REMAINS_V2_END */

</style>
